import React, { useEffect, useState } from 'react';

import { useI18n } from '@oneaudi/i18n-context';
import {
  ExpandableElement,
  FootnoteReference,
  useServicesContext,
  CompleteVehicleEntry,
} from '@oneaudi/vtp-shared';

import { registerImpressionTracking, trackDrawerClick, trackFeatureAppReady } from './tracking';
import { TrackingServiceV2 } from '@oneaudi/audi-tracking-service';
import { Headline } from '@oneaudi/unified-web-components';
import { SYS_SPACE_FIXED_300 } from '@oneaudi/unified-web-common';
import { TechDataList } from './TechnicalDataListPdp';
import { ContentService } from '@oneaudi/content-service';
import { Module, TechnicalDataContainer } from './TechnicalDataPDP.styles';

interface TechnicalDataProps {
  vehicleRaw: CompleteVehicleEntry;
}
export interface TechdataConfiguration {
  fields: {
    scsTechData: string;
    additionalInfoUrl: string;
  };
}

export const TechnicalData: React.FC<TechnicalDataProps> = ({ vehicleRaw }) => {
  const headline = useI18n({
    id: 'nemo.ui.sc.details.technical-data.headline',
    defaultMessage: 'Technical data',
  });
  const techDataRaw = vehicleRaw?.detail?.techData;
  const techData = techDataRaw?.fields;
  const damages = vehicleRaw?.detail?.damages;
  const pollutionBadge = vehicleRaw?.basic?.pollutionBadge?.code;
  const dimensions = vehicleRaw?.detail?.vehicleDimensions;

  // Footnote should only be displayed when car is a used car
  const usedCar = vehicleRaw?.basic?.used !== undefined;
  const [isExpanded, setIsExpanded] = useState(false);

  const trackingService = useServicesContext().getAdditionalService(
    'audi-tracking-service'
  ) as TrackingServiceV2;
  const lessLabel = useI18n({
    id: 'nemo.ui.sc.expandable.button.label.less',
    defaultMessage: 'show less information',
  });
  const moreLabel = useI18n({
    id: 'nemo.ui.sc.expandable.button.label.more',
    defaultMessage: 'show more information',
  });
  useEffect(() => {
    trackFeatureAppReady(trackingService);
    registerImpressionTracking(trackingService, ref);
  }, []);
  const trackButtonClick = () => {
    trackDrawerClick(trackingService, isExpanded, isExpanded ? lessLabel : moreLabel);
    setIsExpanded(!isExpanded);
  };

  useEffect(() => {
    const contentRendered = new CustomEvent('content:rendered');
    if (techData?.length) {
      document.dispatchEvent(contentRendered);
    }
  }, [techData]);

  const ref = React.useRef(null);
  const servicesContext = useServicesContext();
  const audiContentService = servicesContext.getAdditionalService(
    'audi-content-service'
  ) as ContentService;
  const scsTechdataInitial = audiContentService.getContent()?.fields
    ?.scsTechdataInitial as TechdataConfiguration[];
  const scsTechdataExtended = audiContentService.getContent()?.fields
    ?.scsTechdataExtended as TechdataConfiguration[];
  return !scsTechdataInitial || scsTechdataInitial.length === 0 ? null : (
    <TechnicalDataContainer ref={ref}>
      {techData && (
        <Module
          data-audi-core-tracking-include="false"
          className="fa-vtp-technical-data"
          data-testid="TechDataFA"
        >
          <Headline
            tag="h2"
            variant={{ order: '3', type: 'headline', weight: 'normal' }}
            margin={[0, 0, SYS_SPACE_FIXED_300, 0]}
          >
            {headline}
            {usedCar && <FootnoteReference refId="fn_original_state_stockcar" />}
          </Headline>
          <TechDataList
            vehicle={vehicleRaw.basic}
            techDataKeys={scsTechdataInitial}
            isInitial
            techDataItems={techData}
            pollutionBadge={pollutionBadge}
            damages={damages}
            showProductSafetyInfo={
              scsTechdataInitial?.filter((entry) => {
                return entry.fields.scsTechData === 'owners-manual-url';
              }).length > 0
            }
            dimensions={dimensions}
            dataTestId="techDataItemsShort"
          />
          {!scsTechdataExtended || scsTechdataExtended.length === 0 ? null : (
            <ExpandableElement
              isInitiallyOpened={isExpanded}
              buttonPosition="bottom"
              onClick={trackButtonClick}
              isPdp
            >
              <TechDataList
                techDataKeys={scsTechdataExtended}
                isInitial={false}
                techDataItems={techData}
                dimensions={dimensions}
                vehicle={vehicleRaw.basic}
                showProductSafetyInfo={
                  scsTechdataExtended?.filter((entry) => {
                    return entry.fields.scsTechData === 'owners-manual-url';
                  }).length > 0
                }
                dataTestId="techDataItemsExpanded"
              />
            </ExpandableElement>
          )}
        </Module>
      )}
    </TechnicalDataContainer>
  );
};
